import { useEffect, useState, useCallback, useRef, Fragment } from 'react';
import {
    Container, Typography, Card, CardContent, Stack, Box, Button, Chip, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, MenuItem, Drawer, Alert, InputAdornment, Tooltip,
    CircularProgress, Grid, Divider, Stepper, Step, StepLabel, Select, FormControl, InputLabel,
    List, ListItem, ListItemText, ListItemSecondaryAction, Tab, Tabs, Autocomplete,
    Checkbox, FormControlLabel,
} from '@mui/material';
import { Icon } from '@iconify/react';
import searchFill from '@iconify/icons-eva/search-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import plusFill from '@iconify/icons-eva/plus-fill';
import eyeFill from '@iconify/icons-eva/eye-fill';
import refreshFill from '@iconify/icons-eva/refresh-fill';
import uploadFill from '@iconify/icons-eva/upload-fill';
import trashFill from '@iconify/icons-eva/trash-2-fill';
import arrowForwardFill from '@iconify/icons-eva/arrow-forward-fill';
import Page from 'src/components/Page';
import { adminSyndicationApi } from 'src/utils/syndicationApi';
import apiInstance from 'src/utils/apiAuth';
import { showToast, showErrorToast } from 'src/utils/Toast/SnackbarProvider';
import SyndicationDocumentViewer from 'src/components/syndication/SyndicationDocumentViewer';

const STATUS_COLORS = {
    draft: 'default',
    active: 'info',
    presentation: 'primary',
    qa_phase: 'warning',
    commitment_phase: 'secondary',
    allocating: 'info',
    closed: 'success',
    cancelled: 'error',
};

const STATUS_LABELS = {
    draft: 'Draft',
    active: 'Active',
    presentation: 'Presentation',
    qa_phase: 'Q&A Phase',
    commitment_phase: 'Commitment',
    allocating: 'Allocating',
    closed: 'Closed',
    cancelled: 'Cancelled',
};

const INSTRUMENT_LABELS = {
    equity: 'Equity',
    convertible_note: 'Convertible Note',
    safe: 'SAFE',
    revenue_share: 'Revenue Share',
};

const DEAL_STAGES_ORDER = ['draft', 'active', 'presentation', 'qa_phase', 'commitment_phase', 'allocating', 'closed'];

const STAGE_SHORT_LABELS = {
    draft: 'Draft',
    active: 'Active',
    presentation: 'Presentation',
    qa_phase: 'Q&A',
    commitment_phase: 'Commitment',
    allocating: 'Allocating',
    closed: 'Closed',
};

const ADMIN_NEXT_STEPS = {
    draft: { label: 'Next: Upload documents & activate deal', color: 'info.main', icon: '\u270F\uFE0F' },
    active: { label: 'Next: Invite investors & advance to Presentation', color: 'info.main', icon: '\u{1F4E8}' },
    presentation: { label: 'Next: Share materials, then advance to Q&A', color: 'primary.main', icon: '\u{1F4CB}' },
    qa_phase: { label: 'Next: Answer investor questions, then advance to Commitment', color: 'warning.main', icon: '\u2753' },
    commitment_phase: { label: 'Next: Review commitments, then advance to Allocating', color: 'secondary.main', icon: '\u{1F4B0}' },
    allocating: { label: 'Next: Confirm allocations & close deal', color: 'info.main', icon: '\u2696\uFE0F' },
    closed: { label: 'Deal complete \u2014 manage post-investment space', color: 'success.main', icon: '\u2705' },
    cancelled: { label: 'Deal cancelled', color: 'error.main', icon: '\u274C' },
};

const EMPTY_DEAL_FORM = {
    company: '',
    title: '',
    description: '',
    currency: '',
    total_raise: '',
    gateway_share: '',
    coinvestor_allocation: '',
    min_ticket_size: '',
    max_ticket_size: '',
    pre_money_valuation: '',
    post_money_valuation: '',
    equity_offered_percent: '',
    instrument_type: 'equity',
    operator_value_description: '',
    commitment_deadline: '',
    qa_duration_hours: 72,
    commitment_window_hours: 72,
};

export default function AdminDealList() {
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [apiError, setApiError] = useState('');

    // Create Deal Wizard
    const [createDrawer, setCreateDrawer] = useState(false);
    const [wizardStep, setWizardStep] = useState(0);
    const [dealForm, setDealForm] = useState({ ...EMPTY_DEAL_FORM });
    const [createLoading, setCreateLoading] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [currencies, setCurrencies] = useState([]);

    // Detail / Management Drawer
    const [detailDrawer, setDetailDrawer] = useState({ open: false, deal: null });
    const [detailTab, setDetailTab] = useState(0);
    const [detailLoading, setDetailLoading] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [activityLog, setActivityLog] = useState([]);
    // Phase 4 detail state
    const [qaQuestions, setQaQuestions] = useState([]);
    const [dealCommitments, setDealCommitments] = useState([]);
    const [allocSuggestions, setAllocSuggestions] = useState(null);
    const [capTable, setCapTable] = useState([]);
    const [answerText, setAnswerText] = useState('');
    const [answeringId, setAnsweringId] = useState(null);
    const [capForm, setCapForm] = useState({ holder_name: '', holder_type: 'coinvestor', amount: '', equity_percent: '', instrument_type: 'equity', phase: 'post_deal' });
    // Phase 4: Post-Investment state
    const [postInvestment, setPostInvestment] = useState(null);
    const [postInvestmentLoading, setPostInvestmentLoading] = useState(false);
    // Phase 5: Post-Investment upload + publish forms
    const [piDocForm, setPiDocForm] = useState({ title: '', category: 'other', file: null, requires_signature: false });
    const [piDocUploading, setPiDocUploading] = useState(false);
    const [piUpdateForm, setPiUpdateForm] = useState({ title: '', content: '', update_type: 'general', is_published: true });
    const [piUpdatePublishing, setPiUpdatePublishing] = useState(false);

    // Invite drawer
    const [inviteDrawer, setInviteDrawer] = useState(false);
    const [availableInvestors, setAvailableInvestors] = useState([]);
    const [selectedInvestors, setSelectedInvestors] = useState([]);
    const [inviteLoading, setInviteLoading] = useState(false);

    // Company documents (for linking)
    const [companyDocs, setCompanyDocs] = useState([]);
    const [companyDocsLoading, setCompanyDocsLoading] = useState(false);
    const [selectedCompanyDocs, setSelectedCompanyDocs] = useState([]);
    const [linkingDocs, setLinkingDocs] = useState(false);

    // Upload doc
    const [uploadDialog, setUploadDialog] = useState(false);
    const [uploadForm, setUploadForm] = useState({ title: '', category: 'other', file: null });
    const [uploadLoading, setUploadLoading] = useState(false);

    // Status / Cancel
    const [statusDialog, setStatusDialog] = useState({ open: false, deal: null });
    const [cancelDialog, setCancelDialog] = useState({ open: false, deal: null });
    const [cancelReason, setCancelReason] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    // ---- Fetch Deals ----
    const fetchDeals = useCallback(() => {
        setLoading(true);
        setApiError('');
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (statusFilter) params.append('status', statusFilter);

        adminSyndicationApi.get(`admin/deals/?${params.toString()}`)
            .then((res) => {
                const data = res.data;
                setDeals(data.data || data.results || []);
            })
            .catch((err) => {
                setApiError(err.response?.data?.message || 'Failed to load deals.');
            })
            .finally(() => setLoading(false));
    }, [search, statusFilter]);

    useEffect(() => { fetchDeals(); }, [fetchDeals]);

    // ---- Fetch Companies for wizard (searchable, 10 at a time, debounced) ----
    const [companyLoading, setCompanyLoading] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const companyDebounceRef = useRef(null);

    const fetchCompanies = (search = '') => {
        setCompanyLoading(true);
        const q = search ? `admin/company-lookup/?page_size=10&search=${encodeURIComponent(search)}` : 'admin/company-lookup/?page_size=10';
        adminSyndicationApi.get(q)
            .then(res => setCompanies(res.data?.results || []))
            .catch((err) => console.error('Failed to fetch companies:', err))
            .finally(() => setCompanyLoading(false));
    };

    const debouncedFetchCompanies = (val) => {
        if (companyDebounceRef.current) clearTimeout(companyDebounceRef.current);
        companyDebounceRef.current = setTimeout(() => fetchCompanies(val), 400);
    };

    // ---- Fetch Currencies for wizard (searchable, debounced) ----
    const [currencyLoading, setCurrencyLoading] = useState(false);
    const [selectedCurrency, setSelectedCurrency] = useState(null);
    const currencyDebounceRef = useRef(null);

    const fetchCurrencies = (search = '') => {
        setCurrencyLoading(true);
        const q = search ? `currency/?page_size=10&search=${encodeURIComponent(search)}` : 'currency/?page_size=10';
        apiInstance.get(q)
            .then(res => setCurrencies(res.data?.data || res.data?.results || []))
            .catch((err) => console.error('Failed to fetch currencies:', err))
            .finally(() => setCurrencyLoading(false));
    };

    const debouncedFetchCurrencies = (val) => {
        if (currencyDebounceRef.current) clearTimeout(currencyDebounceRef.current);
        currencyDebounceRef.current = setTimeout(() => fetchCurrencies(val), 400);
    };

    // ---- Create Deal ----
    const handleCreateDeal = () => {
        setCreateLoading(true);
        const payload = { ...dealForm };
        // Remove empty optional fields
        Object.keys(payload).forEach(k => {
            if (payload[k] === '' || payload[k] === null) delete payload[k];
        });

        adminSyndicationApi.post('admin/deals/', payload)
            .then((res) => {
                showToast('Deal created successfully!');
                setCreateDrawer(false);
                setDealForm({ ...EMPTY_DEAL_FORM });
                setWizardStep(0);
                fetchDeals();
            })
            .catch((err) => {
                showErrorToast(err.response?.data?.message || err.response?.data?.detail || 'Failed to create deal.');
            })
            .finally(() => setCreateLoading(false));
    };

    // ---- Deal Detail ----
    const openDealDetail = (deal) => {
        setDetailDrawer({ open: true, deal });
        setDetailTab(0);
        fetchDealDocuments(deal.id);
        fetchDealInvitations(deal.id);
        fetchDealActivityLog(deal.id);
        fetchDealQA(deal.id);
        fetchDealCommitments(deal.id);
        fetchCapTable(deal.id);
        fetchPostInvestment(deal.id);
        fetchCompanyDocsForDeal(deal.id);
    };

    const fetchDealDocuments = (dealId) => {
        adminSyndicationApi.get(`admin/deals/${dealId}/documents/`)
            .then(res => setDocuments(res.data.data || []))
            .catch(() => {});
    };

    const fetchCompanyDocsForDeal = (dealId) => {
        setCompanyDocsLoading(true);
        setSelectedCompanyDocs([]);
        adminSyndicationApi.get(`admin/deals/${dealId}/company-documents/`)
            .then(res => setCompanyDocs(res.data.data || []))
            .catch(() => setCompanyDocs([]))
            .finally(() => setCompanyDocsLoading(false));
    };

    const handleToggleCompanyDoc = (docId) => {
        setSelectedCompanyDocs(prev =>
            prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
        );
    };

    const handleLinkCompanyDocs = () => {
        if (!detailDrawer.deal || selectedCompanyDocs.length === 0) return;
        setLinkingDocs(true);
        adminSyndicationApi.post(`admin/deals/${detailDrawer.deal.id}/link-company-documents/`, {
            document_ids: selectedCompanyDocs,
        })
            .then(res => {
                showToast(res.data.message || 'Documents linked!');
                setSelectedCompanyDocs([]);
                fetchDealDocuments(detailDrawer.deal.id);
                fetchCompanyDocsForDeal(detailDrawer.deal.id);
            })
            .catch(err => showErrorToast(err.response?.data?.message || 'Failed to link documents.'))
            .finally(() => setLinkingDocs(false));
    };

    const handleUnlinkCompanyDoc = (companyDocId) => {
        if (!detailDrawer.deal) return;
        adminSyndicationApi.post(`admin/deals/${detailDrawer.deal.id}/unlink-company-documents/`, {
            document_ids: [companyDocId],
        })
            .then(res => {
                showToast(res.data.message || 'Document unlinked!');
                fetchDealDocuments(detailDrawer.deal.id);
                fetchCompanyDocsForDeal(detailDrawer.deal.id);
            })
            .catch(err => showErrorToast(err.response?.data?.message || 'Failed to unlink document.'));
    };

    const handleDeleteDealDocument = (docId, docTitle) => {
        if (!detailDrawer.deal) return;
        if (!window.confirm(`Delete "${docTitle}" from this deal?`)) return;
        adminSyndicationApi.delete(`admin/deals/${detailDrawer.deal.id}/documents/${docId}/delete/`)
            .then(res => {
                showToast(res.data.message || 'Document deleted!');
                fetchDealDocuments(detailDrawer.deal.id);
                fetchCompanyDocsForDeal(detailDrawer.deal.id);
            })
            .catch(err => showErrorToast(err.response?.data?.message || 'Failed to delete document.'));
    };

    const fetchDealInvitations = (dealId) => {
        adminSyndicationApi.get(`admin/deals/${dealId}/invitations/`)
            .then(res => setInvitations(res.data.data || []))
            .catch(() => {});
    };

    const fetchDealActivityLog = (dealId) => {
        adminSyndicationApi.get(`admin/deals/${dealId}/activity-log/`)
            .then(res => setActivityLog(res.data.data || []))
            .catch(() => {});
    };

    // ---- Phase 4: Fetch Q&A ----
    const fetchDealQA = (dealId) => {
        adminSyndicationApi.get(`admin/deals/${dealId}/qa/`)
            .then(res => setQaQuestions(res.data.data || []))
            .catch(() => {});
    };

    // ---- Phase 4: Fetch Commitments ----
    const fetchDealCommitments = (dealId) => {
        adminSyndicationApi.get(`admin/deals/${dealId}/commitments/`)
            .then(res => setDealCommitments(res.data.data || []))
            .catch(() => {});
    };

    // ---- Phase 4: Fetch Allocation Suggestions ----
    const fetchAllocSuggestions = (dealId) => {
        adminSyndicationApi.get(`admin/deals/${dealId}/allocation/suggest/`)
            .then(res => setAllocSuggestions(res.data.data || null))
            .catch(() => {});
    };

    // ---- Phase 4: Fetch Cap Table ----
    const fetchCapTable = (dealId) => {
        adminSyndicationApi.get(`admin/deals/${dealId}/cap-table/`)
            .then(res => setCapTable(res.data.data || []))
            .catch(() => {});
    };

    // ---- Phase 4: Fetch Post-Investment Space ----
    const fetchPostInvestment = (dealId) => {
        setPostInvestmentLoading(true);
        adminSyndicationApi.get(`admin/deals/${dealId}/post-investment/`)
            .then(res => setPostInvestment(res.data.data || null))
            .catch(() => setPostInvestment(null))
            .finally(() => setPostInvestmentLoading(false));
    };

    // ---- Phase 5: Upload Post-Investment Document ----
    const handleUploadPIDocument = (dealId) => {
        if (!piDocForm.title || !piDocForm.file) return;
        setPiDocUploading(true);
        const formData = new FormData();
        formData.append('title', piDocForm.title);
        formData.append('category', piDocForm.category);
        formData.append('file', piDocForm.file);
        formData.append('requires_signature', piDocForm.requires_signature);
        adminSyndicationApi.post(`admin/deals/${dealId}/post-investment/documents/`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
            .then(() => { showToast('Document uploaded.'); setPiDocForm({ title: '', category: 'other', file: null, requires_signature: false }); fetchPostInvestment(dealId); })
            .catch(err => showErrorToast(err.response?.data?.message || 'Upload failed.'))
            .finally(() => setPiDocUploading(false));
    };

    // ---- Phase 5: Publish Investor Update ----
    const handlePublishUpdate = (dealId) => {
        if (!piUpdateForm.title || !piUpdateForm.content) return;
        setPiUpdatePublishing(true);
        adminSyndicationApi.post(`admin/deals/${dealId}/post-investment/updates/`, piUpdateForm)
            .then(() => { showToast('Update published.'); setPiUpdateForm({ title: '', content: '', update_type: 'general', is_published: true }); fetchPostInvestment(dealId); })
            .catch(err => showErrorToast(err.response?.data?.message || 'Publish failed.'))
            .finally(() => setPiUpdatePublishing(false));
    };

    // ---- Phase 4: Approve / Reject Question ----
    const handleApproveQuestion = (dealId, qId) => {
        adminSyndicationApi.post(`admin/deals/${dealId}/qa/${qId}/approve/`)
            .then(() => { showToast('Question approved.'); fetchDealQA(dealId); })
            .catch(err => showErrorToast(err.response?.data?.message || 'Failed.'));
    };
    const handleRejectQuestion = (dealId, qId) => {
        adminSyndicationApi.post(`admin/deals/${dealId}/qa/${qId}/reject/`, { reason: 'Rejected by admin' })
            .then(() => { showToast('Question rejected.'); fetchDealQA(dealId); })
            .catch(err => showErrorToast(err.response?.data?.message || 'Failed.'));
    };

    // ---- Phase 4: Answer Question ----
    const handleAnswerQuestion = (dealId, qId) => {
        if (!answerText.trim()) return;
        adminSyndicationApi.post(`admin/deals/${dealId}/qa/${qId}/answer/`, { answer_text: answerText })
            .then(() => { showToast('Answer submitted.'); setAnswerText(''); setAnsweringId(null); fetchDealQA(dealId); })
            .catch(err => showErrorToast(err.response?.data?.message || 'Failed.'));
    };

    // ---- Phase 4: Confirm Allocation ----
    const handleConfirmAllocation = (dealId) => {
        if (!window.confirm('Confirm allocation? This will notify all investors.')) return;
        adminSyndicationApi.post(`admin/deals/${dealId}/allocation/confirm/`)
            .then(res => { showToast(res.data.message || 'Allocation confirmed.'); fetchDealCommitments(dealId); fetchCapTable(dealId); })
            .catch(err => showErrorToast(err.response?.data?.message || 'Failed.'));
    };

    // ---- Phase 4: Admin Convert Conditional Commitment (Decision 30) ----
    const handleAdminConvertCommitment = (dealId, commitmentId, newDecision) => {
        if (!window.confirm(`Convert this commitment to ${newDecision}?`)) return;
        adminSyndicationApi.post(`admin/deals/${dealId}/commitments/${commitmentId}/convert/`, { new_decision: newDecision })
            .then(res => { showToast(res.data.message || 'Commitment converted.'); fetchDealCommitments(dealId); })
            .catch(err => showErrorToast(err.response?.data?.message || 'Failed.'));
    };

    // ---- Phase 4: Add Cap Table Entry ----
    const handleAddCapEntry = (dealId) => {
        if (!capForm.holder_name || !capForm.amount) return;
        adminSyndicationApi.post(`admin/deals/${dealId}/cap-table/add/`, capForm)
            .then(() => {
                showToast('Entry added.');
                setCapForm({ holder_name: '', holder_type: 'coinvestor', amount: '', equity_percent: '', instrument_type: 'equity', phase: 'post_deal' });
                fetchCapTable(dealId);
            })
            .catch(err => showErrorToast(err.response?.data?.message || 'Failed.'));
    };

    // ---- Phase 4: Delete Cap Table Entry ----
    const handleDeleteCapEntry = (dealId, entryId) => {
        if (!window.confirm('Delete this cap table entry?')) return;
        adminSyndicationApi.delete(`admin/deals/${dealId}/cap-table/${entryId}/delete/`)
            .then(() => { showToast('Entry deleted.'); fetchCapTable(dealId); })
            .catch(err => showErrorToast(err.response?.data?.message || 'Failed.'));
    };

    // ---- Upload Document ----
    const handleUploadDocument = () => {
        if (!detailDrawer.deal) return;
        setUploadLoading(true);
        const formData = new FormData();
        formData.append('title', uploadForm.title);
        formData.append('category', uploadForm.category);
        formData.append('file', uploadForm.file);

        adminSyndicationApi.post(
            `admin/deals/${detailDrawer.deal.id}/documents/upload/`,
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        )
            .then(() => {
                showToast('Document uploaded!');
                setUploadDialog(false);
                setUploadForm({ title: '', category: 'other', file: null });
                fetchDealDocuments(detailDrawer.deal.id);
            })
            .catch(err => showErrorToast(err.response?.data?.message || 'Upload failed.'))
            .finally(() => setUploadLoading(false));
    };

    // ---- Invite Investors ----
    const openInviteDrawer = () => {
        setInviteDrawer(true);
        setSelectedInvestors([]);
        adminSyndicationApi.get('admin/investors/?status=active&page_size=500')
            .then(res => {
                const data = res.data.data || res.data.results || [];
                setAvailableInvestors(data);
            })
            .catch(() => {});
    };

    const handleInviteInvestors = () => {
        if (!detailDrawer.deal || selectedInvestors.length === 0) return;
        setInviteLoading(true);
        adminSyndicationApi.post(`admin/deals/${detailDrawer.deal.id}/invite/`, {
            investor_ids: selectedInvestors,
        })
            .then(res => {
                showToast(res.data.message || 'Investors invited!');
                setInviteDrawer(false);
                fetchDealInvitations(detailDrawer.deal.id);
            })
            .catch(err => showErrorToast(err.response?.data?.message || 'Failed to invite.'))
            .finally(() => setInviteLoading(false));
    };

    // ---- Revoke Invitation ----
    const handleRevokeInvitation = (invId) => {
        if (!detailDrawer.deal) return;
        adminSyndicationApi.post(`admin/deals/${detailDrawer.deal.id}/invitations/${invId}/revoke/`)
            .then(() => {
                showToast('Invitation revoked.');
                fetchDealInvitations(detailDrawer.deal.id);
            })
            .catch(err => showErrorToast(err.response?.data?.message || 'Revoke failed.'));
    };

    // ---- Advance Status ----
    const handleAdvanceStatus = (newStatus) => {
        if (!statusDialog.deal) return;
        setActionLoading(true);
        adminSyndicationApi.post(`admin/deals/${statusDialog.deal.id}/status/`, {
            new_status: newStatus,
        })
            .then(res => {
                showToast(res.data.message || 'Status updated!');
                setStatusDialog({ open: false, deal: null });
                fetchDeals();
                if (detailDrawer.deal && detailDrawer.deal.id === statusDialog.deal.id) {
                    setDetailDrawer(prev => ({
                        ...prev,
                        deal: { ...prev.deal, status: newStatus },
                    }));
                }
            })
            .catch(err => showErrorToast(err.response?.data?.message || 'Status change failed.'))
            .finally(() => setActionLoading(false));
    };

    // ---- Cancel Deal ----
    const handleCancelDeal = () => {
        if (!cancelDialog.deal) return;
        setActionLoading(true);
        adminSyndicationApi.post(`admin/deals/${cancelDialog.deal.id}/cancel/`, {
            reason: cancelReason,
        })
            .then(res => {
                showToast(res.data.message || 'Deal cancelled.');
                setCancelDialog({ open: false, deal: null });
                setCancelReason('');
                fetchDeals();
            })
            .catch(err => showErrorToast(err.response?.data?.message || 'Cancel failed.'))
            .finally(() => setActionLoading(false));
    };

    // ---- Valid Transitions ----
    const VALID_TRANSITIONS = {
        draft: ['active'],
        active: ['presentation'],
        presentation: ['qa_phase'],
        qa_phase: ['commitment_phase'],
        commitment_phase: ['allocating'],
        allocating: ['closed'],
    };

    const handleStepClick = (deal, targetStatus) => {
        if (!window.confirm(`Advance "${deal.title}" from ${STATUS_LABELS[deal.status]} to ${STATUS_LABELS[targetStatus]}?`)) return;
        setActionLoading(true);
        adminSyndicationApi.post(`admin/deals/${deal.id}/status/`, { new_status: targetStatus })
            .then(res => {
                showToast(res.data.message || 'Status updated!');
                fetchDeals();
                if (detailDrawer.deal && detailDrawer.deal.id === deal.id) {
                    setDetailDrawer(prev => ({ ...prev, deal: { ...prev.deal, status: targetStatus } }));
                }
            })
            .catch(err => showErrorToast(err.response?.data?.message || 'Status change failed.'))
            .finally(() => setActionLoading(false));
    };

    const formatCurrency = (val, currency) => {
        if (!val) return '-';
        const num = Number(val);
        if (num >= 1000000) return `${currency || ''} ${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${currency || ''} ${(num / 1000).toFixed(0)}K`;
        return `${currency || ''} ${num.toLocaleString()}`;
    };

    const wizardSteps = ['Basic Info', 'Financial Structure', 'Timeline & Settings'];

    return (
        <Page title="Syndication Deals">
            <Container maxWidth="xl">
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
                    <Typography variant="h4">Syndication Deals</Typography>
                    <Button
                        variant="contained"
                        startIcon={<Icon icon={plusFill} />}
                        onClick={() => {
                            setCreateDrawer(true);
                            setWizardStep(0);
                            setDealForm({ ...EMPTY_DEAL_FORM });
                            setSelectedCompany(null);
                            setSelectedCurrency(null);
                            setCompanies([]);
                            setCurrencies([]);
                        }}
                    >
                        Create Deal
                    </Button>
                </Stack>

                {apiError && <Alert severity="error" sx={{ mb: 2 }}>{apiError}</Alert>}

                {/* Filters */}
                <Card sx={{ p: 2, mb: 3 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Search deals..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon icon={searchFill} width={20} height={20} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ minWidth: 260 }}
                        />
                        <TextField
                            select size="small" label="Status" value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            sx={{ minWidth: 160 }}
                        >
                            <MenuItem value="">All</MenuItem>
                            {Object.entries(STATUS_LABELS).map(([k, v]) => (
                                <MenuItem key={k} value={k}>{v}</MenuItem>
                            ))}
                        </TextField>
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchDeals}>
                                <Icon icon={refreshFill} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Card>

                {/* Deal Table */}
                <Card>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Code</TableCell>
                                    <TableCell>Title</TableCell>
                                    <TableCell>Company</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Total Raise</TableCell>
                                    <TableCell>Instrument</TableCell>
                                    <TableCell>Deadline</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : deals.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                                            <Typography color="text.secondary">No deals found.</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    deals.map((deal) => {
                                        const currentStageIdx = DEAL_STAGES_ORDER.indexOf(deal.status);
                                        return (
                                        <Fragment key={deal.id}>
                                            <TableRow hover>
                                                <TableCell>
                                                    <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
                                                        {deal.deal_code}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{deal.title}</TableCell>
                                                <TableCell>{deal.company_name}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={STATUS_LABELS[deal.status] || deal.status}
                                                        color={STATUS_COLORS[deal.status] || 'default'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {formatCurrency(deal.total_raise, deal.currency_label)}
                                                </TableCell>
                                                <TableCell>
                                                    {INSTRUMENT_LABELS[deal.instrument_type] || deal.instrument_type}
                                                </TableCell>
                                                <TableCell>
                                                    {deal.commitment_deadline
                                                        ? new Date(deal.commitment_deadline).toLocaleDateString()
                                                        : '-'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Tooltip title="View / Manage">
                                                        <IconButton onClick={() => openDealDetail(deal)}>
                                                            <Icon icon={eyeFill} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {deal.status !== 'cancelled' && deal.status !== 'closed' && (
                                                        <Tooltip title="Cancel Deal">
                                                            <IconButton
                                                                color="error"
                                                                onClick={() => setCancelDialog({ open: true, deal })}
                                                            >
                                                                <Icon icon={trashFill} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell colSpan={8} sx={{ pt: 0, pb: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                                                    {deal.status === 'cancelled' ? (
                                                        <Chip label="Cancelled" color="error" size="small" variant="outlined" />
                                                    ) : (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            {DEAL_STAGES_ORDER.map((stage, idx) => {
                                                                const isCompleted = idx < currentStageIdx;
                                                                const isCurrent = idx === currentStageIdx;
                                                                const isNext = (VALID_TRANSITIONS[deal.status] || []).includes(stage);
                                                                return (
                                                                    <Fragment key={stage}>
                                                                        {idx > 0 && (
                                                                            <Box sx={{ flex: 1, height: 2, bgcolor: idx <= currentStageIdx ? 'success.main' : 'grey.300', minWidth: 8 }} />
                                                                        )}
                                                                        <Tooltip title={isNext ? `Click to advance to ${STAGE_SHORT_LABELS[stage]}` : isCurrent ? 'Current stage' : STAGE_SHORT_LABELS[stage]}>
                                                                            <Box
                                                                                onClick={isNext ? () => handleStepClick(deal, stage) : undefined}
                                                                                sx={{
                                                                                    px: 1, py: 0.25, borderRadius: 1, fontSize: '0.7rem', fontWeight: isCurrent ? 700 : 500,
                                                                                    cursor: isNext ? 'pointer' : 'default',
                                                                                    bgcolor: isCompleted ? 'rgba(84,214,44,0.16)' : isCurrent ? 'rgba(255,171,0,0.16)' : isNext ? 'rgba(24,144,255,0.08)' : 'grey.100',
                                                                                    color: isCompleted ? 'success.dark' : isCurrent ? 'warning.dark' : isNext ? 'primary.main' : 'text.disabled',
                                                                                    border: isNext ? '1.5px dashed' : '1.5px solid transparent',
                                                                                    borderColor: isNext ? 'primary.main' : 'transparent',
                                                                                    '&:hover': isNext ? { bgcolor: 'primary.main', color: '#fff' } : {},
                                                                                    transition: 'all 0.2s', whiteSpace: 'nowrap',
                                                                                }}
                                                                            >
                                                                                {isCompleted && '\u2713 '}{STAGE_SHORT_LABELS[stage]}
                                                                            </Box>
                                                                        </Tooltip>
                                                                    </Fragment>
                                                                );
                                                            })}
                                                        </Box>
                                                    )}
                                                    {ADMIN_NEXT_STEPS[deal.status] && (
                                                        <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: ADMIN_NEXT_STEPS[deal.status].color, fontWeight: 600 }}>
                                                            {ADMIN_NEXT_STEPS[deal.status].icon} {ADMIN_NEXT_STEPS[deal.status].label}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        </Fragment>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>

                {/* =================== CREATE DEAL WIZARD DRAWER =================== */}
                <Drawer
                    anchor="right"
                    open={createDrawer}
                    onClose={() => setCreateDrawer(false)}
                    PaperProps={{ sx: { width: { xs: '100%', md: 600 } } }}
                >
                    <Box sx={{ p: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h5">Create New Deal</Typography>
                            <IconButton onClick={() => setCreateDrawer(false)}>
                                <Icon icon={closeFill} />
                            </IconButton>
                        </Stack>

                        <Stepper activeStep={wizardStep} sx={{ mb: 3 }}>
                            {wizardSteps.map((label) => (
                                <Step key={label}><StepLabel>{label}</StepLabel></Step>
                            ))}
                        </Stepper>

                        {/* Step 0: Basic Info */}
                        {wizardStep === 0 && (
                            <Stack spacing={2}>
                                <Autocomplete
                                    options={companies}
                                    getOptionLabel={(opt) => opt.company_name || ''}
                                    value={selectedCompany}
                                    loading={companyLoading}
                                    onOpen={() => { if (companies.length === 0) fetchCompanies(); }}
                                    onInputChange={(e, val, reason) => {
                                        if (reason === 'input') debouncedFetchCompanies(val);
                                    }}
                                    onChange={(e, val) => {
                                        setSelectedCompany(val);
                                        setDealForm(f => ({ ...f, company: val?.id || '' }));
                                    }}
                                    isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Company" required fullWidth
                                            InputProps={{ ...params.InputProps, endAdornment: (
                                                <>{companyLoading ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>
                                            )}}
                                        />
                                    )}
                                    filterOptions={(x) => x}
                                    noOptionsText="Type to search..."
                                    fullWidth
                                />
                                <TextField
                                    label="Deal Title" fullWidth required
                                    value={dealForm.title}
                                    onChange={e => setDealForm(f => ({ ...f, title: e.target.value }))}
                                />
                                <TextField
                                    label="Description" fullWidth multiline rows={3}
                                    value={dealForm.description}
                                    onChange={e => setDealForm(f => ({ ...f, description: e.target.value }))}
                                />
                                <TextField
                                    select label="Instrument Type" fullWidth
                                    value={dealForm.instrument_type}
                                    onChange={e => setDealForm(f => ({ ...f, instrument_type: e.target.value }))}
                                >
                                    {Object.entries(INSTRUMENT_LABELS).map(([k, v]) => (
                                        <MenuItem key={k} value={k}>{v}</MenuItem>
                                    ))}
                                </TextField>
                                <Autocomplete
                                    options={currencies}
                                    getOptionLabel={(opt) => opt.label || opt.name || opt.code || ''}
                                    value={selectedCurrency}
                                    loading={currencyLoading}
                                    onOpen={() => { if (currencies.length === 0) fetchCurrencies(); }}
                                    onInputChange={(e, val, reason) => {
                                        if (reason === 'input') debouncedFetchCurrencies(val);
                                    }}
                                    onChange={(e, val) => {
                                        setSelectedCurrency(val);
                                        setDealForm(f => ({ ...f, currency: val?.id || '' }));
                                    }}
                                    isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Currency" required fullWidth
                                            InputProps={{ ...params.InputProps, endAdornment: (
                                                <>{currencyLoading ? <CircularProgress size={18} /> : null}{params.InputProps.endAdornment}</>
                                            )}}
                                        />
                                    )}
                                    filterOptions={(x) => x}
                                    fullWidth
                                />
                                <Button
                                    variant="contained" fullWidth
                                    disabled={!dealForm.company || !dealForm.title || !dealForm.currency}
                                    onClick={() => setWizardStep(1)}
                                >
                                    Next: Financial Structure
                                </Button>
                            </Stack>
                        )}

                        {/* Step 1: Financial Structure */}
                        {wizardStep === 1 && (
                            <Stack spacing={2}>
                                <TextField
                                    label="Total Raise" type="number" fullWidth required
                                    value={dealForm.total_raise}
                                    onChange={e => setDealForm(f => ({ ...f, total_raise: e.target.value }))}
                                />
                                <TextField
                                    label="Gateway Share" type="number" fullWidth required
                                    value={dealForm.gateway_share}
                                    onChange={e => setDealForm(f => ({ ...f, gateway_share: e.target.value }))}
                                />
                                <TextField
                                    label="Co-investor Allocation" type="number" fullWidth required
                                    value={dealForm.coinvestor_allocation}
                                    onChange={e => setDealForm(f => ({ ...f, coinvestor_allocation: e.target.value }))}
                                />
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Min Ticket Size" type="number" fullWidth
                                            value={dealForm.min_ticket_size}
                                            onChange={e => setDealForm(f => ({ ...f, min_ticket_size: e.target.value }))}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Max Ticket Size" type="number" fullWidth
                                            value={dealForm.max_ticket_size}
                                            onChange={e => setDealForm(f => ({ ...f, max_ticket_size: e.target.value }))}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Pre-Money Valuation" type="number" fullWidth
                                            value={dealForm.pre_money_valuation}
                                            onChange={e => setDealForm(f => ({ ...f, pre_money_valuation: e.target.value }))}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Post-Money Valuation" type="number" fullWidth
                                            value={dealForm.post_money_valuation}
                                            onChange={e => setDealForm(f => ({ ...f, post_money_valuation: e.target.value }))}
                                        />
                                    </Grid>
                                </Grid>
                                <TextField
                                    label="Equity Offered %" type="number" fullWidth
                                    value={dealForm.equity_offered_percent}
                                    onChange={e => setDealForm(f => ({ ...f, equity_offered_percent: e.target.value }))}
                                />
                                <Stack direction="row" spacing={2}>
                                    <Button variant="outlined" fullWidth onClick={() => setWizardStep(0)}>Back</Button>
                                    <Button
                                        variant="contained" fullWidth
                                        disabled={!dealForm.total_raise || !dealForm.gateway_share || !dealForm.coinvestor_allocation}
                                        onClick={() => setWizardStep(2)}
                                    >
                                        Next: Timeline
                                    </Button>
                                </Stack>
                            </Stack>
                        )}

                        {/* Step 2: Timeline & Settings */}
                        {wizardStep === 2 && (
                            <Stack spacing={2}>
                                <TextField
                                    label="Commitment Deadline"
                                    type="datetime-local" fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    value={dealForm.commitment_deadline}
                                    onChange={e => setDealForm(f => ({ ...f, commitment_deadline: e.target.value }))}
                                />
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Q&A Duration (hours)" type="number" fullWidth
                                            value={dealForm.qa_duration_hours}
                                            onChange={e => setDealForm(f => ({ ...f, qa_duration_hours: e.target.value }))}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label="Commitment Window (hours)" type="number" fullWidth
                                            value={dealForm.commitment_window_hours}
                                            onChange={e => setDealForm(f => ({ ...f, commitment_window_hours: e.target.value }))}
                                        />
                                    </Grid>
                                </Grid>
                                <TextField
                                    label="Operator Value Description" fullWidth multiline rows={3}
                                    value={dealForm.operator_value_description}
                                    onChange={e => setDealForm(f => ({ ...f, operator_value_description: e.target.value }))}
                                />
                                <Stack direction="row" spacing={2}>
                                    <Button variant="outlined" fullWidth onClick={() => setWizardStep(1)}>Back</Button>
                                    <Button
                                        variant="contained" fullWidth
                                        onClick={handleCreateDeal}
                                        disabled={createLoading}
                                    >
                                        {createLoading ? <CircularProgress size={24} /> : 'Create Deal'}
                                    </Button>
                                </Stack>
                            </Stack>
                        )}
                    </Box>
                </Drawer>

                {/* =================== DEAL DETAIL / MANAGEMENT DRAWER =================== */}
                <Drawer
                    anchor="right"
                    open={detailDrawer.open}
                    onClose={() => setDetailDrawer({ open: false, deal: null })}
                    PaperProps={{ sx: { width: { xs: '100%', md: '90vw' } } }}
                >
                    {detailDrawer.deal && (
                        <Box sx={{ p: 3 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                <Box>
                                    <Typography variant="h5">{detailDrawer.deal.title}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {detailDrawer.deal.deal_code} &middot; {detailDrawer.deal.company_name}
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip
                                        label={STATUS_LABELS[detailDrawer.deal.status]}
                                        color={STATUS_COLORS[detailDrawer.deal.status]}
                                        size="small"
                                    />
                                    <IconButton onClick={() => setDetailDrawer({ open: false, deal: null })}>
                                        <Icon icon={closeFill} />
                                    </IconButton>
                                </Stack>
                            </Stack>

                            <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} sx={{ mb: 2 }}>
                                <Tab label="Overview" />
                                <Tab label={`Documents (${documents.length})`} />
                                <Tab label={`Investors (${invitations.length})`} />
                                <Tab label="Activity" />
                                <Tab label={`Q&A (${qaQuestions.length})`} />
                                <Tab label={`Commitments (${dealCommitments.length})`} />
                                <Tab label="Allocation" />
                                <Tab label={`Cap Table (${capTable.length})`} />
                                <Tab label="Post-Investment" />
                            </Tabs>

                            {/* Tab 0: Overview */}
                            {detailTab === 0 && (
                                <Stack spacing={2}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Total Raise</Typography>
                                            <Typography variant="h6">
                                                {formatCurrency(detailDrawer.deal.total_raise, detailDrawer.deal.currency_label)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Co-investor Allocation</Typography>
                                            <Typography variant="h6">
                                                {formatCurrency(detailDrawer.deal.coinvestor_allocation, detailDrawer.deal.currency_label)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Gateway Share</Typography>
                                            <Typography>
                                                {formatCurrency(detailDrawer.deal.gateway_share, detailDrawer.deal.currency_label)}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Instrument</Typography>
                                            <Typography>
                                                {INSTRUMENT_LABELS[detailDrawer.deal.instrument_type]}
                                            </Typography>
                                        </Grid>
                                        {detailDrawer.deal.pre_money_valuation && (
                                            <Grid item xs={6}>
                                                <Typography variant="caption" color="text.secondary">Pre-Money Valuation</Typography>
                                                <Typography>
                                                    {formatCurrency(detailDrawer.deal.pre_money_valuation, detailDrawer.deal.currency_label)}
                                                </Typography>
                                            </Grid>
                                        )}
                                        {detailDrawer.deal.commitment_deadline && (
                                            <Grid item xs={6}>
                                                <Typography variant="caption" color="text.secondary">Commitment Deadline</Typography>
                                                <Typography>
                                                    {new Date(detailDrawer.deal.commitment_deadline).toLocaleString()}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                    {detailDrawer.deal.description && (
                                        <Box>
                                            <Typography variant="caption" color="text.secondary">Description</Typography>
                                            <Typography variant="body2">{detailDrawer.deal.description}</Typography>
                                        </Box>
                                    )}
                                </Stack>
                            )}

                            {/* Tab 1: Documents */}
                            {detailTab === 1 && (
                                <Box>
                                    {/* Deal Documents Section */}
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Deal Documents ({documents.length})</Typography>
                                        <Button
                                            size="small" variant="contained"
                                            startIcon={<Icon icon={uploadFill} />}
                                            onClick={() => setUploadDialog(true)}
                                        >
                                            Upload New
                                        </Button>
                                    </Stack>
                                    {documents.length === 0 ? (
                                        <Typography color="text.secondary" sx={{ mb: 3 }}>No documents attached to this deal yet.</Typography>
                                    ) : (
                                        <TableContainer component={Card} variant="outlined" sx={{ mb: 3 }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Title</TableCell>
                                                        <TableCell>Category</TableCell>
                                                        <TableCell>Type</TableCell>
                                                        <TableCell>Version</TableCell>
                                                        <TableCell>Status</TableCell>
                                                        <TableCell align="center">Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {documents.map(doc => (
                                                        <TableRow key={doc.id}>
                                                            <TableCell>{doc.title}</TableCell>
                                                            <TableCell>{doc.category}</TableCell>
                                                            <TableCell>{doc.file_type}</TableCell>
                                                            <TableCell>v{doc.current_version}</TableCell>
                                                            <TableCell>
                                                                <Chip
                                                                    label={doc.is_approved ? 'Approved' : 'Pending'}
                                                                    color={doc.is_approved ? 'success' : 'warning'}
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                                                                    <SyndicationDocumentViewer
                                                                        fileUrl={doc.file}
                                                                        fileType={doc.file_type}
                                                                        title={doc.title}
                                                                        showDownload={false}
                                                                        iconOnly
                                                                    />
                                                                    <Tooltip title="Delete document">
                                                                        <IconButton
                                                                            size="small"
                                                                            color="error"
                                                                            onClick={() => handleDeleteDealDocument(doc.id, doc.title)}
                                                                        >
                                                                            <Icon icon={trashFill} width={18} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Stack>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}

                                    {/* Company Documents Section */}
                                    <Divider sx={{ my: 2 }} />
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                Company Documents ({companyDocs.length})
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Select existing documents from {detailDrawer.deal?.company_name || 'the company'} to link to this deal
                                            </Typography>
                                        </Box>
                                        {selectedCompanyDocs.length > 0 && (
                                            <Button
                                                size="small" variant="contained" color="primary"
                                                onClick={handleLinkCompanyDocs}
                                                disabled={linkingDocs}
                                            >
                                                {linkingDocs ? <CircularProgress size={18} /> : `Link ${selectedCompanyDocs.length} Selected`}
                                            </Button>
                                        )}
                                    </Stack>
                                    {companyDocsLoading ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
                                    ) : companyDocs.length === 0 ? (
                                        <Typography color="text.secondary">No documents found for this company.</Typography>
                                    ) : (
                                        <TableContainer component={Card} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                size="small"
                                                                indeterminate={selectedCompanyDocs.length > 0 && selectedCompanyDocs.length < companyDocs.filter(d => !d.is_linked).length}
                                                                checked={companyDocs.filter(d => !d.is_linked).length > 0 && selectedCompanyDocs.length === companyDocs.filter(d => !d.is_linked).length}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setSelectedCompanyDocs(companyDocs.filter(d => !d.is_linked).map(d => d.id));
                                                                    } else {
                                                                        setSelectedCompanyDocs([]);
                                                                    }
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>File Name</TableCell>
                                                        <TableCell>Type</TableCell>
                                                        <TableCell>Description</TableCell>
                                                        <TableCell>Uploaded</TableCell>
                                                        <TableCell>Status</TableCell>
                                                        <TableCell align="center">Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {companyDocs.map(cdoc => (
                                                        <TableRow
                                                            key={cdoc.id}
                                                            hover
                                                            selected={selectedCompanyDocs.includes(cdoc.id)}
                                                            sx={{ opacity: cdoc.is_linked ? 0.6 : 1 }}
                                                        >
                                                            <TableCell padding="checkbox">
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={cdoc.is_linked || selectedCompanyDocs.includes(cdoc.id)}
                                                                    disabled={cdoc.is_linked}
                                                                    onChange={() => handleToggleCompanyDoc(cdoc.id)}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                                                                    {cdoc.file_name || 'Untitled'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Chip label={cdoc.type || 'OTHER'} size="small" variant="outlined" />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                                                    {cdoc.description || '—'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                {cdoc.uploaded_on ? new Date(cdoc.uploaded_on).toLocaleDateString() : '—'}
                                                            </TableCell>
                                                            <TableCell>
                                                                {cdoc.is_linked ? (
                                                                    <Chip label="Linked" color="success" size="small" />
                                                                ) : (
                                                                    <Chip label="Available" size="small" variant="outlined" />
                                                                )}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                                                                    <SyndicationDocumentViewer
                                                                        fileUrl={cdoc.file_url}
                                                                        fileType={cdoc.type}
                                                                        title={cdoc.file_name || 'Document'}
                                                                        showDownload={false}
                                                                        iconOnly
                                                                        size="small"
                                                                    />
                                                                    {cdoc.is_linked && (
                                                                        <Tooltip title="Unlink from deal">
                                                                            <IconButton
                                                                                size="small"
                                                                                color="error"
                                                                                onClick={() => handleUnlinkCompanyDoc(cdoc.id)}
                                                                            >
                                                                                <Icon icon={closeFill} width={18} />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    )}
                                                                </Stack>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Box>
                            )}

                            {/* Tab 2: Investors / Invitations */}
                            {detailTab === 2 && (
                                <Box>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="subtitle1">Invited Investors</Typography>
                                        {detailDrawer.deal.status !== 'cancelled' && detailDrawer.deal.status !== 'closed' && (
                                            <Button
                                                size="small" variant="contained"
                                                startIcon={<Icon icon={plusFill} />}
                                                onClick={openInviteDrawer}
                                            >
                                                Invite
                                            </Button>
                                        )}
                                    </Stack>
                                    {invitations.length === 0 ? (
                                        <Typography color="text.secondary">No investors invited yet.</Typography>
                                    ) : (
                                        <List>
                                            {invitations.map(inv => (
                                                <ListItem key={inv.id} divider>
                                                    <ListItemText
                                                        primary={inv.investor_name}
                                                        secondary={`${inv.investor_email} · ${inv.investor_type || ''} · Accessed ${inv.access_count}x`}
                                                    />
                                                    <ListItemSecondaryAction>
                                                        {inv.revoked ? (
                                                            <Chip label="Revoked" color="error" size="small" />
                                                        ) : (
                                                            <Tooltip title="Revoke Access">
                                                                <IconButton
                                                                    color="error" size="small"
                                                                    onClick={() => handleRevokeInvitation(inv.id)}
                                                                >
                                                                    <Icon icon={closeFill} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </ListItemSecondaryAction>
                                                </ListItem>
                                            ))}
                                        </List>
                                    )}
                                </Box>
                            )}

                            {/* Tab 3: Activity Log */}
                            {detailTab === 3 && (
                                <Box>
                                    <Typography variant="subtitle1" mb={2}>Activity Log</Typography>
                                    {activityLog.length === 0 ? (
                                        <Typography color="text.secondary">No activity recorded.</Typography>
                                    ) : (
                                        <List dense>
                                            {activityLog.map(log => (
                                                <ListItem key={log.id} divider>
                                                    <ListItemText
                                                        primary={log.description || log.action}
                                                        secondary={`${log.actor_name || 'System'} · ${log.actor_role} · ${new Date(log.created).toLocaleString()}`}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    )}
                                </Box>
                            )}

                            {/* Tab 4: Q&A */}
                            {detailTab === 4 && (
                                <Box>
                                    <Typography variant="subtitle1" mb={2}>Q&A Management</Typography>
                                    {qaQuestions.length === 0 ? (
                                        <Typography color="text.secondary">No questions yet.</Typography>
                                    ) : (
                                        <Stack spacing={2}>
                                            {qaQuestions.map(q => (
                                                <Card key={q.id} variant="outlined">
                                                    <CardContent sx={{ pb: '12px !important' }}>
                                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                                            <Box sx={{ flex: 1 }}>
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>Q: {q.question_text}</Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    By {q.asked_by_name || 'Investor'} · {q.asked_at ? new Date(q.asked_at).toLocaleDateString() : ''}
                                                                </Typography>
                                                            </Box>
                                                            <Stack direction="row" spacing={0.5}>
                                                                {!q.is_approved && !q.is_rejected && (
                                                                    <>
                                                                        <Button size="small" variant="contained" color="success"
                                                                            onClick={() => handleApproveQuestion(detailDrawer.deal.id, q.id)}>
                                                                            Approve
                                                                        </Button>
                                                                        <Button size="small" variant="outlined" color="error"
                                                                            onClick={() => handleRejectQuestion(detailDrawer.deal.id, q.id)}>
                                                                            Reject
                                                                        </Button>
                                                                    </>
                                                                )}
                                                                {q.is_approved && <Chip label="Approved" color="success" size="small" />}
                                                                {q.is_rejected && <Chip label="Rejected" color="error" size="small" />}
                                                            </Stack>
                                                        </Stack>
                                                        {q.answers && q.answers.map((a, i) => (
                                                            <Box key={i} sx={{ mt: 1, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                                                                <Typography variant="body2">A: {a.answer_text}</Typography>
                                                                <Typography variant="caption" color="text.secondary">by {a.answered_by_role}</Typography>
                                                            </Box>
                                                        ))}
                                                        {q.is_approved && (
                                                            answeringId === q.id ? (
                                                                <Stack direction="row" spacing={1} mt={1}>
                                                                    <TextField size="small" fullWidth placeholder="Type answer..."
                                                                        value={answerText} onChange={e => setAnswerText(e.target.value)} />
                                                                    <Button size="small" variant="contained"
                                                                        onClick={() => handleAnswerQuestion(detailDrawer.deal.id, q.id)}>Send</Button>
                                                                    <Button size="small" onClick={() => { setAnsweringId(null); setAnswerText(''); }}>Cancel</Button>
                                                                </Stack>
                                                            ) : (
                                                                <Button size="small" sx={{ mt: 1 }} onClick={() => setAnsweringId(q.id)}>Answer</Button>
                                                            )
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            ))}
                                        </Stack>
                                    )}
                                </Box>
                            )}

                            {/* Tab 5: Commitments */}
                            {detailTab === 5 && (
                                <Box>
                                    <Typography variant="subtitle1" mb={2}>Commitments</Typography>
                                    {dealCommitments.length === 0 ? (
                                        <Typography color="text.secondary">No commitments yet.</Typography>
                                    ) : (
                                        <TableContainer component={Card} variant="outlined">
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Investor</TableCell>
                                                        <TableCell>Type</TableCell>
                                                        <TableCell align="right">Amount</TableCell>
                                                        <TableCell align="right">Allocated</TableCell>
                                                        <TableCell>Status</TableCell>
                                                        <TableCell>Signed</TableCell>
                                                        <TableCell align="center">Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {dealCommitments.map(c => (
                                                        <TableRow key={c.id}>
                                                            <TableCell>{c.investor_name || c.investor_email}</TableCell>
                                                            <TableCell><Chip label={c.decision} size="small" variant="outlined" /></TableCell>
                                                            <TableCell align="right">{Number(c.amount || 0).toLocaleString()}</TableCell>
                                                            <TableCell align="right">{c.allocated_amount != null ? Number(c.allocated_amount).toLocaleString() : '—'}</TableCell>
                                                            <TableCell>
                                                                {c.is_withdrawn ? <Chip label="Withdrawn" color="error" size="small" /> :
                                                                 c.is_locked ? <Chip label="Locked" color="warning" size="small" /> :
                                                                 <Chip label="Active" color="success" size="small" />}
                                                            </TableCell>
                                                            <TableCell>{c.signature_confirmed ? 'Yes' : 'No'}</TableCell>
                                                            <TableCell align="center">
                                                                {c.decision === 'conditional' && !c.is_locked && !c.is_withdrawn && (
                                                                    <Stack direction="row" spacing={0.5} justifyContent="center">
                                                                        <Button size="small" variant="outlined"
                                                                            onClick={() => handleAdminConvertCommitment(detailDrawer.deal.id, c.id, 'soft')}>
                                                                            → Soft
                                                                        </Button>
                                                                        <Button size="small" variant="outlined"
                                                                            onClick={() => handleAdminConvertCommitment(detailDrawer.deal.id, c.id, 'binding')}>
                                                                            → Binding
                                                                        </Button>
                                                                    </Stack>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Box>
                            )}

                            {/* Tab 6: Allocation */}
                            {detailTab === 6 && (
                                <Box>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                                        <Typography variant="subtitle1">Allocation Engine</Typography>
                                        <Stack direction="row" spacing={1}>
                                            <Button size="small" variant="outlined"
                                                onClick={() => fetchAllocSuggestions(detailDrawer.deal.id)}>
                                                Generate Suggestions
                                            </Button>
                                            <Button size="small" variant="contained" color="success"
                                                onClick={() => handleConfirmAllocation(detailDrawer.deal.id)}>
                                                Confirm Allocation
                                            </Button>
                                        </Stack>
                                    </Stack>
                                    {allocSuggestions ? (
                                        <>
                                            <Stack direction="row" spacing={2} mb={2}>
                                                <Card variant="outlined" sx={{ p: 1.5, flex: 1 }}>
                                                    <Typography variant="caption" color="text.secondary">Available</Typography>
                                                    <Typography variant="h6">{Number(allocSuggestions.available_allocation || 0).toLocaleString()}</Typography>
                                                </Card>
                                                <Card variant="outlined" sx={{ p: 1.5, flex: 1 }}>
                                                    <Typography variant="caption" color="text.secondary">Remaining</Typography>
                                                    <Typography variant="h6">{Number(allocSuggestions.remaining_after_suggestion || 0).toLocaleString()}</Typography>
                                                </Card>
                                            </Stack>
                                            <TableContainer component={Card} variant="outlined">
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Investor</TableCell>
                                                            <TableCell>Type</TableCell>
                                                            <TableCell align="right">Requested</TableCell>
                                                            <TableCell align="right">Suggested</TableCell>
                                                            <TableCell>Signed</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {(allocSuggestions.suggestions || []).map((s, i) => (
                                                            <TableRow key={i}>
                                                                <TableCell>{s.investor_name}</TableCell>
                                                                <TableCell><Chip label={s.decision} size="small" variant="outlined" /></TableCell>
                                                                <TableCell align="right">{Number(s.requested_amount || 0).toLocaleString()}</TableCell>
                                                                <TableCell align="right">{Number(s.suggested_amount || 0).toLocaleString()}</TableCell>
                                                                <TableCell>{s.signature_confirmed ? 'Yes' : 'No'}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </>
                                    ) : (
                                        <Typography color="text.secondary">Click "Generate Suggestions" to see auto-calculated allocations.</Typography>
                                    )}
                                </Box>
                            )}

                            {/* Tab 7: Cap Table */}
                            {detailTab === 7 && (
                                <Box>
                                    <Typography variant="subtitle1" mb={2}>Cap Table</Typography>
                                    {capTable.length > 0 && (
                                        <TableContainer component={Card} variant="outlined" sx={{ mb: 2 }}>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Holder</TableCell>
                                                        <TableCell>Type</TableCell>
                                                        <TableCell align="right">Amount</TableCell>
                                                        <TableCell align="right">Equity %</TableCell>
                                                        <TableCell>Instrument</TableCell>
                                                        <TableCell align="center">Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {capTable.map(e => (
                                                        <TableRow key={e.id}>
                                                            <TableCell>{e.holder_name}</TableCell>
                                                            <TableCell><Chip label={e.holder_type} size="small" variant="outlined" /></TableCell>
                                                            <TableCell align="right">{Number(e.amount || 0).toLocaleString()}</TableCell>
                                                            <TableCell align="right">{e.equity_percent}%</TableCell>
                                                            <TableCell>{e.instrument_type}</TableCell>
                                                            <TableCell align="center">
                                                                {e.is_manual && (
                                                                    <IconButton size="small" color="error"
                                                                        onClick={() => handleDeleteCapEntry(detailDrawer.deal.id, e.id)}>
                                                                        <Icon icon={trashFill} width={16} />
                                                                    </IconButton>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Add Entry</Typography>
                                            <Grid container spacing={1.5}>
                                                <Grid item xs={6}>
                                                    <TextField size="small" fullWidth label="Holder Name"
                                                        value={capForm.holder_name}
                                                        onChange={e => setCapForm(f => ({ ...f, holder_name: e.target.value }))} />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <TextField size="small" fullWidth select label="Holder Type"
                                                        value={capForm.holder_type}
                                                        onChange={e => setCapForm(f => ({ ...f, holder_type: e.target.value }))}>
                                                        <MenuItem value="founder">Founder / Existing</MenuItem>
                                                        <MenuItem value="gateway">Gateway X</MenuItem>
                                                        <MenuItem value="coinvestor">Co-investor</MenuItem>
                                                        <MenuItem value="esop">ESOP Pool</MenuItem>
                                                        <MenuItem value="other">Other</MenuItem>
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <TextField size="small" fullWidth label="Amount" type="number"
                                                        value={capForm.amount}
                                                        onChange={e => setCapForm(f => ({ ...f, amount: e.target.value }))} />
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <TextField size="small" fullWidth label="Equity %" type="number"
                                                        value={capForm.equity_percent}
                                                        onChange={e => setCapForm(f => ({ ...f, equity_percent: e.target.value }))} />
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <TextField size="small" fullWidth select label="Instrument"
                                                        value={capForm.instrument_type}
                                                        onChange={e => setCapForm(f => ({ ...f, instrument_type: e.target.value }))}>
                                                        <MenuItem value="equity">Equity</MenuItem>
                                                        <MenuItem value="convertible_note">Convertible Note</MenuItem>
                                                        <MenuItem value="safe">SAFE</MenuItem>
                                                        <MenuItem value="revenue_share">Revenue Share</MenuItem>
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <TextField size="small" fullWidth select label="Phase"
                                                        value={capForm.phase}
                                                        onChange={e => setCapForm(f => ({ ...f, phase: e.target.value }))}>
                                                        <MenuItem value="pre_deal">Pre-Deal (Existing)</MenuItem>
                                                        <MenuItem value="post_deal">Post-Deal (Projected)</MenuItem>
                                                    </TextField>
                                                </Grid>
                                            </Grid>
                                            <Button size="small" variant="contained" sx={{ mt: 1.5 }}
                                                onClick={() => handleAddCapEntry(detailDrawer.deal.id)}
                                                disabled={!capForm.holder_name || !capForm.amount}>
                                                Add Entry
                                            </Button>
                                        </CardContent>
                                    </Card>
                                </Box>
                            )}

                            {/* Tab 8: Post-Investment */}
                            {detailTab === 8 && (
                                <Box>
                                    {postInvestmentLoading ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                                    ) : !postInvestment ? (
                                        <Alert severity="info">Post-investment space is not yet available. Deal must be closed first.</Alert>
                                    ) : (
                                        <Stack spacing={2}>
                                            <Alert severity="success">Post-investment space is active for this deal.</Alert>

                                            {/* Existing Documents */}
                                            {postInvestment.documents && postInvestment.documents.length > 0 ? (
                                                <Card variant="outlined">
                                                    <CardContent>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Documents ({postInvestment.documents.length})</Typography>
                                                        <List dense>
                                                            {postInvestment.documents.map(doc => (
                                                                <ListItem key={doc.id} secondaryAction={
                                                                    <SyndicationDocumentViewer
                                                                        fileUrl={doc.file}
                                                                        fileType={doc.file_type}
                                                                        title={doc.title}
                                                                        showDownload={false}
                                                                        iconOnly
                                                                        size="small"
                                                                    />
                                                                }>
                                                                    <ListItemText primary={doc.title} secondary={`${doc.category} | ${doc.file_type} | ${(doc.file_size / 1024).toFixed(1)} KB${doc.requires_signature ? ' | Signature Required' : ''}`} />
                                                                </ListItem>
                                                            ))}
                                                        </List>
                                                    </CardContent>
                                                </Card>
                                            ) : (
                                                <Typography color="text.secondary" variant="body2">No post-investment documents uploaded yet.</Typography>
                                            )}

                                            {/* Upload Document Form */}
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Upload Legal / Governance Document</Typography>
                                                    <Stack spacing={1.5}>
                                                        <TextField size="small" label="Document Title" fullWidth value={piDocForm.title} onChange={e => setPiDocForm(f => ({ ...f, title: e.target.value }))} />
                                                        <TextField size="small" select label="Category" fullWidth value={piDocForm.category} onChange={e => setPiDocForm(f => ({ ...f, category: e.target.value }))}>
                                                            <MenuItem value="sha">Shareholders Agreement</MenuItem>
                                                            <MenuItem value="subscription">Subscription Agreement</MenuItem>
                                                            <MenuItem value="board_resolution">Board Resolution</MenuItem>
                                                            <MenuItem value="compliance">Compliance Document</MenuItem>
                                                            <MenuItem value="other">Other</MenuItem>
                                                        </TextField>
                                                        <Button size="small" variant="outlined" component="label">
                                                            {piDocForm.file ? piDocForm.file.name : 'Choose File'}
                                                            <input type="file" hidden accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.png,.mp4" onChange={e => setPiDocForm(f => ({ ...f, file: e.target.files[0] }))} />
                                                        </Button>
                                                        <Button size="small" variant="contained" disabled={!piDocForm.title || !piDocForm.file || piDocUploading} onClick={() => handleUploadPIDocument(detailDrawer.deal.id)}>
                                                            {piDocUploading ? <CircularProgress size={18} /> : 'Upload Document'}
                                                        </Button>
                                                    </Stack>
                                                </CardContent>
                                            </Card>

                                            {/* Existing Updates */}
                                            {postInvestment.updates && postInvestment.updates.length > 0 ? (
                                                <Card variant="outlined">
                                                    <CardContent>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Investor Updates ({postInvestment.updates.length})</Typography>
                                                        <List dense>
                                                            {postInvestment.updates.map(upd => (
                                                                <ListItem key={upd.id}>
                                                                    <ListItemText primary={upd.title} secondary={`${upd.update_type} | ${upd.published_at ? new Date(upd.published_at).toLocaleDateString() : 'Draft'}`} />
                                                                </ListItem>
                                                            ))}
                                                        </List>
                                                    </CardContent>
                                                </Card>
                                            ) : (
                                                <Typography color="text.secondary" variant="body2">No investor updates published yet.</Typography>
                                            )}

                                            {/* Publish Update Form */}
                                            <Card variant="outlined">
                                                <CardContent>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Publish Investor Update</Typography>
                                                    <Stack spacing={1.5}>
                                                        <TextField size="small" label="Update Title" fullWidth value={piUpdateForm.title} onChange={e => setPiUpdateForm(f => ({ ...f, title: e.target.value }))} />
                                                        <TextField size="small" select label="Update Type" fullWidth value={piUpdateForm.update_type} onChange={e => setPiUpdateForm(f => ({ ...f, update_type: e.target.value }))}>
                                                            <MenuItem value="general">General Update</MenuItem>
                                                            <MenuItem value="financial">Financial Update</MenuItem>
                                                            <MenuItem value="quarterly">Quarterly Report</MenuItem>
                                                            <MenuItem value="milestone">Milestone Update</MenuItem>
                                                        </TextField>
                                                        <TextField size="small" label="Content" fullWidth multiline rows={4} value={piUpdateForm.content} onChange={e => setPiUpdateForm(f => ({ ...f, content: e.target.value }))} />
                                                        <Button size="small" variant="contained" color="primary" disabled={!piUpdateForm.title || !piUpdateForm.content || piUpdatePublishing} onClick={() => handlePublishUpdate(detailDrawer.deal.id)}>
                                                            {piUpdatePublishing ? <CircularProgress size={18} /> : 'Publish Update'}
                                                        </Button>
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        </Stack>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}
                </Drawer>

                {/* =================== UPLOAD DOCUMENT DRAWER =================== */}
                <Drawer
                    anchor="right"
                    open={uploadDialog}
                    onClose={() => setUploadDialog(false)}
                    PaperProps={{ sx: { width: { xs: '100%', md: 420 } } }}
                >
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="h6">Upload Document</Typography>
                            <IconButton onClick={() => setUploadDialog(false)} size="small">
                                <Icon icon={closeFill} />
                            </IconButton>
                        </Box>
                        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                            <Stack spacing={2}>
                                <TextField
                                    label="Document Title" fullWidth required
                                    value={uploadForm.title}
                                    onChange={e => setUploadForm(f => ({ ...f, title: e.target.value }))}
                                />
                                <TextField
                                    select label="Category" fullWidth
                                    value={uploadForm.category}
                                    onChange={e => setUploadForm(f => ({ ...f, category: e.target.value }))}
                                >
                                    <MenuItem value="pitch_deck">Pitch Deck</MenuItem>
                                    <MenuItem value="financial_model">Financial Model</MenuItem>
                                    <MenuItem value="term_sheet">Term Sheet</MenuItem>
                                    <MenuItem value="legal">Legal Document</MenuItem>
                                    <MenuItem value="due_diligence">Due Diligence</MenuItem>
                                    <MenuItem value="other">Other</MenuItem>
                                </TextField>
                                <Button variant="outlined" component="label">
                                    {uploadForm.file ? uploadForm.file.name : 'Choose File'}
                                    <input
                                        type="file" hidden
                                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.png,.mp4"
                                        onChange={e => setUploadForm(f => ({ ...f, file: e.target.files[0] }))}
                                    />
                                </Button>
                            </Stack>
                        </Box>
                        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button onClick={() => setUploadDialog(false)}>Cancel</Button>
                            <Button
                                variant="contained"
                                disabled={!uploadForm.title || !uploadForm.file || uploadLoading}
                                onClick={handleUploadDocument}
                            >
                                {uploadLoading ? <CircularProgress size={20} /> : 'Upload'}
                            </Button>
                        </Box>
                    </Box>
                </Drawer>

                {/* =================== INVITE INVESTORS DRAWER =================== */}
                <Drawer
                    anchor="right"
                    open={inviteDrawer}
                    onClose={() => setInviteDrawer(false)}
                    PaperProps={{ sx: { width: { xs: '100%', md: 480 } } }}
                >
                    <Box sx={{ p: 3 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">Invite Investors</Typography>
                            <IconButton onClick={() => setInviteDrawer(false)}>
                                <Icon icon={closeFill} />
                            </IconButton>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            Select active investors to invite to this deal.
                        </Typography>
                        <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                            {availableInvestors.map(inv => (
                                <ListItem
                                    key={inv.id} button
                                    selected={selectedInvestors.includes(inv.id)}
                                    onClick={() => {
                                        setSelectedInvestors(prev =>
                                            prev.includes(inv.id)
                                                ? prev.filter(id => id !== inv.id)
                                                : [...prev, inv.id]
                                        );
                                    }}
                                >
                                    <ListItemText
                                        primary={`${inv.first_name || inv.user_first_name || ''} ${inv.last_name || inv.user_last_name || ''}`}
                                        secondary={inv.email || inv.user_email || ''}
                                    />
                                </ListItem>
                            ))}
                        </List>
                        <Button
                            variant="contained" fullWidth sx={{ mt: 2 }}
                            disabled={selectedInvestors.length === 0 || inviteLoading}
                            onClick={handleInviteInvestors}
                        >
                            {inviteLoading ? <CircularProgress size={20} /> : `Invite ${selectedInvestors.length} Investor(s)`}
                        </Button>
                    </Box>
                </Drawer>

                {/* =================== STATUS ADVANCE DRAWER =================== */}
                <Drawer
                    anchor="right"
                    open={statusDialog.open}
                    onClose={() => setStatusDialog({ open: false, deal: null })}
                    PaperProps={{ sx: { width: { xs: '100%', md: 420 } } }}
                >
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="h6">Advance Deal Status</Typography>
                            <IconButton onClick={() => setStatusDialog({ open: false, deal: null })} size="small">
                                <Icon icon={closeFill} />
                            </IconButton>
                        </Box>
                        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                            {statusDialog.deal && (
                                <>
                                    <Typography variant="body2" color="text.secondary" mb={2}>
                                        Current: <strong>{STATUS_LABELS[statusDialog.deal.status]}</strong>
                                    </Typography>
                                    <Stack spacing={1}>
                                        {(VALID_TRANSITIONS[statusDialog.deal.status] || []).map(ns => (
                                            <Button
                                                key={ns} variant="outlined" fullWidth
                                                onClick={() => handleAdvanceStatus(ns)}
                                                disabled={actionLoading}
                                            >
                                                Advance to {STATUS_LABELS[ns]}
                                            </Button>
                                        ))}
                                    </Stack>
                                </>
                            )}
                        </Box>
                    </Box>
                </Drawer>

                {/* =================== CANCEL DEAL DRAWER =================== */}
                <Drawer
                    anchor="right"
                    open={cancelDialog.open}
                    onClose={() => setCancelDialog({ open: false, deal: null })}
                    PaperProps={{ sx: { width: { xs: '100%', md: 420 } } }}
                >
                    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="h6">Cancel Deal</Typography>
                            <IconButton onClick={() => { setCancelDialog({ open: false, deal: null }); setCancelReason(''); }} size="small">
                                <Icon icon={closeFill} />
                            </IconButton>
                        </Box>
                        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                Are you sure you want to cancel this deal? All investors will be notified.
                            </Alert>
                            <TextField
                                label="Cancellation Reason (optional)" fullWidth multiline rows={3}
                                value={cancelReason}
                                onChange={e => setCancelReason(e.target.value)}
                            />
                        </Box>
                        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button onClick={() => { setCancelDialog({ open: false, deal: null }); setCancelReason(''); }}>
                                No, Keep Deal
                            </Button>
                            <Button
                                variant="contained" color="error"
                                onClick={handleCancelDeal}
                                disabled={actionLoading}
                            >
                                {actionLoading ? <CircularProgress size={20} /> : 'Cancel Deal'}
                            </Button>
                        </Box>
                    </Box>
                </Drawer>
            </Container>
        </Page>
    );
}
